import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

/** Startup is already busy with auth, sync and window setup — don't compete. */
export const INITIAL_DELAY_MS = 30_000;

/** Nula stays open for days, so a poll is the only way a long session learns. */
export const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

interface Options {
  /** False on web and in secondary windows — there is nothing to update. */
  enabled: boolean;
  isRecording: boolean;
}

interface OpenUpdates {
  /** Opens Settings → Updates. The tab owns install, progress and notes. */
  onOpen?: () => void;
}

/* The found update is shared: UpdateNotifier runs the poll, the settings badge
 * only reads it. Per-hook state left the badge permanently empty. */
let found: { version: string; notes: string | null } | null = null;
const foundListeners = new Set<() => void>();

function subscribeFound(listener: () => void) {
  foundListeners.add(listener);
  return () => foundListeners.delete(listener);
}

function getFound() {
  return found;
}

function setFound(next: { version: string; notes: string | null }) {
  if (found?.version === next.version) return;
  found = next;
  for (const l of foundListeners) l();
}

/** Test-only: the module store outlives a render, so isolation needs a reset. */
export function resetUpdateSchedulerForTests() {
  found = null;
  foundListeners.clear();
}

/**
 * Background update check.
 *
 * Announces each new version once, then stays quiet until a newer one appears
 * — a six-hourly poll must not turn into a six-hourly nag. The version is
 * tracked even when the notification is suppressed so the settings badge can
 * still surface it.
 */
export function useUpdateScheduler({ enabled, isRecording, onOpen }: Options & OpenUpdates) {
  // Module-level so the settings badge can read the result without running a
  // second poll — two enabled schedulers would notify twice.
  const found = useSyncExternalStore(subscribeFound, getFound, getFound);
  const announced = useRef<string | null>(null);
  // Read inside the interval callback so a change does not resubscribe it.
  const suppressed = useRef(isRecording);
  suppressed.current = isRecording;
  const openRef = useRef(onOpen);
  openRef.current = onOpen;

  const runCheck = useCallback(async () => {
    try {
      const { check } = await import('@tauri-apps/plugin-updater');
      const update = (await check()) as { version: string; body?: string } | null;
      if (!update) return;

      setFound({ version: update.version, notes: update.body ?? null });

      if (announced.current === update.version) return;
      if (suppressed.current) return;
      announced.current = update.version;

      const { notification, Button } = await import('@nula/core-ui');
      notification.info({
        key: update.version,
        message: `Nula ${update.version} is available`,
        description: update.body ?? undefined,
        // Persistent: an update the user glanced away from should not vanish.
        duration: 0,
        btn: (
          <Button size="sm" onClick={() => openRef.current?.()}>
            View update
          </Button>
        ),
      });
    } catch {
      // Offline, DNS failure, a bad manifest — none should stop the schedule.
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let interval: ReturnType<typeof setInterval> | undefined;
    const initial = setTimeout(() => {
      void runCheck();
      interval = setInterval(() => void runCheck(), CHECK_INTERVAL_MS);
    }, INITIAL_DELAY_MS);

    return () => {
      clearTimeout(initial);
      if (interval) clearInterval(interval);
    };
  }, [enabled, runCheck]);

  return { availableVersion: found?.version ?? null, notes: found?.notes ?? null };
}
